import { DOCUMENT } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import {
  YFHeaderItem,
  YFHeaderMenuItem,
  YFHeaderSubMenuItem,
} from './models/youforce-header-menu-item.model';

const cssScriptFileName = 'youforce-navigation-bar.css';
const jsScriptFileName = 'youforce-navigation-bar.esm.js';

@Component({
  selector: 'youforce-header',
  standalone: true,
  template: '',
})
export class YouforceHeaderComponent
  implements OnInit, AfterViewInit, OnChanges
{
  @Input() cdnUrl!: string | null;
  @Input() culture!: string | null;
  @Input() subHeaderItems: YFHeaderSubMenuItem[] = [];
  @Input() helpItems: YFHeaderItem[] = [];
  @Input() activeItemId: string | null = null;

  @Output() loadingError = new EventEmitter<string>();
  @Output() subMenuItemClicked = new EventEmitter<YFHeaderMenuItem>();
  @Output() helpMenuItemClicked = new EventEmitter<YFHeaderItem>();
  @Output() logoffClicked = new EventEmitter<void>();
  @Output() isRenderd = new EventEmitter<boolean>(false);

  private readonly elementRef = inject(ElementRef);
  private readonly document = inject<Document>(DOCUMENT);

  private testId = 'youforce-header';
  private headerWebComponent: HTMLElementHeader | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (this.headerWebComponent) {
      if (changes['activeItemId'] && this.activeItemId) {
        this.updateActiveItem();
      }
      this.updateWebComponentProperties();
    }
  }

  ngOnInit(): void {
    this.injectHeaderWebComponent();
  }

  ngAfterViewInit(): void {
    this.updateWebComponentProperties();
    this.bindNavigationBarEvents();
  }

  private bindNavigationBarEvents(): void {
    this.headerWebComponent?.addEventListener('isRendered', () => {
      this.isRenderd.emit(true);
    });

    this.headerWebComponent?.addEventListener(
      'subHeaderItemClicked',
      (event: Event) => {
        const customEvent = event as CustomEvent<YFHeaderMenuItem>;
        this.subMenuItemClicked.emit(customEvent.detail);
      },
    );

    this.headerWebComponent?.addEventListener(
      'helpItemClicked',
      (event: Event) => {
        const customEvent = event as CustomEvent<YFHeaderItem>;
        this.helpMenuItemClicked.emit(customEvent.detail);
      },
    );

    this.headerWebComponent?.addEventListener('logoutClicked', () => {
      this.logoffClicked.emit();
    });
  }

  private updateActiveItem(): void {
    this.subHeaderItems = this.subHeaderItems.map((item) => {
      item.active = item.id === this.activeItemId;

      if (item.subItems) {
        item.subItems = item.subItems.map((subItem) => {
          subItem.active = subItem.id === this.activeItemId;
          if (subItem.active) {
            item.active = true;
          }
          return subItem;
        });
      }
      return item;
    });

    if (this.headerWebComponent) {
      this.headerWebComponent.subHeaderItems = this.subHeaderItems;
    }
  }

  private injectHeaderWebComponent(): void {
    if (!this.cdnUrl) {
      this.loadingError.emit('CDN URL is not provided');
      return;
    }

    const script = this.document.createElement('script');
    script.type = 'module';
    script.src = `${this.cdnUrl}/${jsScriptFileName}`;
    this.document.head.appendChild(script);
    script.onerror = () => {
      this.loadingError.emit('Failed to load the script');
    };

    const link = this.document.createElement('link');
    link.rel = 'stylesheet';
    link.href = `${this.cdnUrl}/${cssScriptFileName}`;
    this.document.head.appendChild(link);

    this.headerWebComponent = this.document.createElement(
      'youforce-navigation-bar',
    ) as HTMLElementHeader;
    this.headerWebComponent.setAttribute('data-test-id', this.testId);
    this.elementRef.nativeElement.appendChild(this.headerWebComponent);
  }

  private updateWebComponentProperties(): void {
    if (this.culture) {
      this.headerWebComponent?.setAttribute('culture', this.culture);
    }

    if (this.subHeaderItems && this.headerWebComponent) {
      this.headerWebComponent.subHeaderItems = this.subHeaderItems;
    }
    if (this.helpItems && this.headerWebComponent) {
      this.headerWebComponent.helpItems = this.helpItems;
    }
  }
}

interface HTMLElementHeader extends HTMLElement {
  helpItems: YFHeaderItem[];
  subHeaderItems: YFHeaderSubMenuItem[];
}
